/**
 * The status vocabulary, read rather than retyped.
 *
 * `src/utils/orderStatus.js` is the ONLY source of truth for what the backend
 * can say. Every label and every colour on this page comes out of
 * `ORDER_STATUS_CONFIG` through `getStatusLabel` / `getStatusColor`, so a
 * status added there shows up here with no edit at all.
 *
 * "Shipped" is not in that file. It appears in older library templates and it
 * is not a value `Shipment.status` can hold, so it appears nowhere on this
 * page and must never be introduced.
 *
 * WHAT THIS FILE ADDS ON TOP OF THE CONFIG
 * ----------------------------------------
 * Two things the config does not model, both of which are presentation:
 *
 *   1. The tone a status pill takes. The config has FIVE colour words across
 *      four buckets plus `primary`; the desktop pill has four tones plus ink.
 *      `toneFor` is the mapping and it is one place, not one per surface.
 *   2. Where a status sits on the customer-facing stepper, which has exactly
 *      four nodes: Collected, In transit, Out for delivery, Delivered.
 */

import { getStatusColor, getStatusLabel } from '../../../utils/orderStatus';

/** The four customer-facing stepper nodes, in order. All real config labels. */
export const TRACKING_STEPS = ['Collected', 'In transit', 'Out for delivery', 'Delivered'];

/**
 * Where each status sits on the four-node stepper.
 *
 * Statuses missing from this table are deliberate, not oversights:
 *
 *   before collection  `awaiting_dropoff`, `pending_collection`,
 *                      `collect_and_return_to_hub`, and the legacy `pending`,
 *                      `paid`, `approved`. Nothing has been collected yet, so
 *                      the stepper renders with no node reached rather than
 *                      claiming a first step that has not happened.
 *   halted             every `error` status. A parcel that was returned,
 *                      cancelled or failed is not somewhere on a line towards
 *                      delivery, so the stepper is replaced by a note. Drawing
 *                      a partial progress bar for a dead parcel is a lie.
 *
 * `ready_for_pickup` shares the third node with `out_for_delivery`: both mean
 * the parcel has finished travelling and is waiting at the far end.
 */
const STEP_INDEX = {
  collected: 0,
  in_transit: 1,
  out_for_delivery: 2,
  ready_for_pickup: 2,
  delivered: 3,
  finalised: 3,
  // Legacy settled values land on the last node for the same reason.
  completed: 3,
  verified: 3,
};

/** Normalise whatever the payload carried into a config key. */
export function normaliseStatus(value) {
  return String(value || '').toLowerCase().trim();
}

/** The display label, straight from the config, with its snake_case fallback. */
export function labelFor(value) {
  return getStatusLabel(normaliseStatus(value));
}

/**
 * Config colour to `Pill` tone.
 *
 * The four buckets pass straight through, because `Pill` already carries all
 * four: `warning`, `info`, `success`, `error`. So does `primary`, which sits on
 * `approved` alone and which `Pill` aliases onto ink.
 *
 * `default` covers a status the config has never heard of. It stays untinted
 * rather than guessing a severity for an unknown word.
 */
export function toneFor(value) {
  const colour = getStatusColor(normaliseStatus(value));
  return ['warning', 'info', 'success', 'error', 'primary'].includes(colour) ? colour : 'default';
}

/**
 * The same status on the mobile track's `StatusPill`, whose tones are named
 * slightly differently: it has no `primary` and no untinted default, and its
 * brand-state tone is called `accent`.
 */
export function mobileToneFor(value) {
  const tone = toneFor(value);
  if (tone === 'primary') return 'accent';
  if (tone === 'default') return 'info';
  return tone;
}

/** True when the parcel is not going to arrive: every `error` bucket status. */
export function isHalted(value) {
  return getStatusColor(normaliseStatus(value)) === 'error';
}

/** True once the parcel has landed, which is what unlocks review and dispute. */
export function isSettled(value) {
  return getStatusColor(normaliseStatus(value)) === 'success';
}

/**
 * The stepper index, or -1 for "nothing reached yet".
 * Returns -1 for halted statuses too; callers check `isHalted` first and draw
 * a note instead of a stepper.
 */
export function stepIndexFor(value) {
  const index = STEP_INDEX[normaliseStatus(value)];
  return index === undefined ? -1 : index;
}
